/**
 * useRealtimeSession
 *
 * React hook that manages a full Azure OpenAI Realtime API WebSocket session
 * for the KI-Interviewer (Marvin).
 *
 * Lifecycle:
 *  1. connect()  → POST /api/python/interview/realtime/session
 *                → receives ephemeral token + endpoint
 *                → opens WebSocket to Azure
 *                → sends session.update (initial instructions, VAD config, …)
 *  2. Audio loop → caller feeds PCM16 base64 chunks via sendAudio()
 *                → forwarded as input_audio_buffer.append events
 *  3. Events     → VAD, transcription, response text delta, response.done
 *  4. Turn end   → POST /api/python/interview/realtime/sync (context-sync)
 *                → session.update with new instructions
 *  5. Reconnect  → exponential backoff on unexpected close / error
 *  6. disconnect → clean up WebSocket + audio state
 *
 * Usage:
 *  const session = useRealtimeSession({
 *    sessionId, workspaceId, topic, token
 *    onTranscriptUpdate: (text) => …,
 *    onResponseToken:    (text) => …,
 *    onTurnComplete:     (transcript, response) => …,
 *    onError:            (msg)  => …,
 *  });
 *
 *  <button onClick={session.connect}>Start</button>
 *  // Wire audio: useRealtimeAudio({ onAudioChunk: session.sendAudio })
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/lib/logger';
import { apiClient } from '@/lib/apiClient';

// ── Types ─────────────────────────────────────────────────────────────────

export interface UseRealtimeSessionOptions {
  sessionId: string;
  workspaceId: string;
  topic?: string;
  templateId?: string;
  /** Feature context — determines which backend endpoint to use. */
  featureContext?: string;
  /** Page context for help feature. */
  pageContext?: string;
  /** Bearer token for backend authentication. */
  token?: string | null;

  /** Called whenever the live user transcript changes (interim + final). */
  onTranscriptUpdate?: (transcript: string, isFinal?: boolean) => void;
  /** Called for each streamed response text token from Marvin. */
  onResponseToken?: (token: string) => void;
  /**
   * Called once a full turn is complete (response.done).
   * @param userTranscript  Final transcription of the user's speech.
   * @param aiResponse      Full AI response text for this turn.
   */
  onTurnComplete?: (userTranscript: string, aiResponse: string) => void;
  /** Called on unrecoverable errors or when all reconnect attempts fail. */
  onError?: (message: string) => void;

  /**
   * Called when the AI invokes the `end_interview` function tool —
   * i.e. the expert said something like "ich bin fertig".
   * The caller should trigger session disconnect + UI cleanup.
   */
  onSessionEndRequested?: () => void;

  /**
   * Called after the per-turn context-sync completes with the entities and
   * relationships extracted by the backend from the realtime turn.
   * Wire to `updateLastTurnEntities` in the interview store.
   */
  onEntitiesExtracted?: (
    entities: Record<string, unknown>[],
    relationships: Record<string, unknown>[],
  ) => void;

  /**
   * Enable audio output modality (spoken AI responses).
   * Maps to the `audioOutput` query param on the WS proxy.
   */
  audioOutput?: boolean;
  /**
   * Voice for spoken responses.
   * Must be one of: alloy, echo, shimmer, verse, ballad.
   * Defaults to 'alloy'.
   */
  voice?: string;
  /**
   * Called for each `response.audio.delta` event with a Base64-encoded
   * PCM16 chunk.  Wire to `useRealtimeAudioPlayback.enqueueChunk`.
   */
  onAudioChunk?: (base64Pcm16: string) => void;
}

export interface UseRealtimeSessionResult {
  /**
   * Open the WebSocket session (fetches ephemeral token first).
   * @param overrideToken  Optional auth token that takes precedence over
   *                       the `token` option.  Pass when the caller must
   *                       acquire a fresh token just before connecting
   *                       (avoids React state-batching ordering issues).
   */
  connect: (overrideToken?: string | null) => Promise<void>;
  /** Close the WebSocket and stop all audio processing. */
  disconnect: () => void;
  /**
   * Send a Base64-encoded PCM16 audio chunk to Azure.
   * No-op when not connected.
   */
  sendAudio: (base64Pcm16: string) => void;
  /**
   * Like sendAudio, but only forwards the chunk when VAD has detected active
   * speech.  Prefer this over manually gating on `isListening` state, which
   * can lag behind due to React batching.
   */
  sendGatedAudio: (base64Pcm16: string) => void;
  /**
   * Manually commit the audio buffer (input_audio_buffer.commit).
   * Triggers VAD-independent end-of-turn.
   */
  commitAudio: () => void;
  /**
   * Manually request a response (response.create).
   * Use together with commitAudio() for push-to-talk / manual override.
   */
  requestResponse: () => void;

  /**
   * Dynamically update the audio output modality and voice for the current
   * session without reconnecting.
   */
  updateAudioSettings: (audioOutput: boolean, voice: string) => void;

  isConnected: boolean;
  /** True when VAD detected speech start. */
  isListening: boolean;
  /** True while Azure is generating a response. */
  isResponding: boolean;
  /** Current live transcript of the user's speech. */
  transcript: string;
  /** Accumulated response tokens for the current turn. */
  responseText: string;
  /** Non-null when an error occurred. */
  error: string | null;
}

// ── Azure Realtime event shapes (minimal) ────────────────────────────────

interface AzureRealtimeEvent {
  type: string;
  [key: string]: unknown;
}

// ── Constants ─────────────────────────────────────────────────────────────

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 1_000;

// ── Sync response type ───────────────────────────────────────────────────

interface RealtimeSyncApiResponse {
  updatedInstructions: string;  /** Entities extracted from the realtime turn (mirrors chat/stream entities). */
  entities?: Record<string, unknown>[];
  relationships?: Record<string, unknown>[];}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useRealtimeSession(
  options: UseRealtimeSessionOptions,
): UseRealtimeSessionResult {
  // ── State ────────────────────────────────────────────────────────────────
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [responseText, setResponseText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ── Refs (do not trigger re-renders) ────────────────────────────────────
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIntentionalCloseRef = useRef(false);
  // Synchronous shadow of isListening state — updated BEFORE React batches the
  // state update so that gatedSendAudio (called per audio chunk) never reads a
  // stale false even in the first few ms after speech_started fires.
  const isListeningRef = useRef(false);

  // Keep latest turn data accessible in stable callbacks.
  const currentTranscriptRef = useRef('');
  const currentResponseRef = useRef('');
  // Deferred-sync: when response.done fires before transcription.completed,
  // store the AI response here and trigger runContextSync once the final
  // transcript arrives (instead of running with an empty userTranscript).
  // State machine: idle → speech_started → speech_stopped → response_created
  //   → response_done → sync_pending / sync_complete → idle (next turn)
  const deferredAiResponseRef = useRef<string | null>(null);
  // Safety: if transcription.completed never arrives (Azure error), this timer
  // fires after 5 s and runs context-sync with whatever transcript we have.
  const deferredSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep options stable in callbacks without re-subscribing effects.
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; }, [options]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const emitError = useCallback((msg: string) => {
    setError(msg);
    optionsRef.current.onError?.(msg);
  }, []);

  const sendEvent = useCallback((event: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(event));
    }
  }, []);

  // ── Context sync after each response.done ────────────────────────────────

  const runContextSync = useCallback(
    async (userTranscript: string, aiResponse: string) => {
      try {
        // Skip context sync for non-interview features — they don't need
        // entity extraction or updated interview instructions.
        const isInterview = !optionsRef.current.featureContext || optionsRef.current.featureContext === 'interview';
        if (!isInterview) return;

        const resp = await apiClient.post<RealtimeSyncApiResponse>(
          '/api/python/interview/realtime/sync',
          {
            token: optionsRef.current.token,
            body: {
              sessionId: optionsRef.current.sessionId,
              workspaceId: optionsRef.current.workspaceId,
              userTranscript,
              aiResponse,
              templateId: optionsRef.current.templateId,
              topic: optionsRef.current.topic,
            },
          },
        );
        if (resp?.updatedInstructions) {
          sendEvent({
            type: 'session.update',
            session: { instructions: resp.updatedInstructions },
          });
        }
        // Forward extracted entities to ChatInput so they appear in the
        // sidebar and are stored on the assistant message (same as text chat).
        if (resp?.entities?.length || resp?.relationships?.length) {
          optionsRef.current.onEntitiesExtracted?.(
            resp.entities ?? [],
            resp.relationships ?? [],
          );
        }
      } catch (err) {
        // Context sync is fire-and-forget — log but don't surface as fatal.
        logger.warn('useRealtimeSession', 'Context sync failed', err);
      }
    },
    [sendEvent],
  );

  // ── Azure event handler ───────────────────────────────────────────────────

  const handleAzureEvent = useCallback(
    (event: AzureRealtimeEvent) => {
      switch (event.type) {
        // VAD: user started speaking
        case 'input_audio_buffer.speech_started':
          isListeningRef.current = true;   // sync — before React batch
          setIsListening(true);
          // Clear previous transcript for new turn
          currentTranscriptRef.current = '';
          setTranscript('');
          // Cancel any pending deferred sync from previous turn (safety guard).
          deferredAiResponseRef.current = null;
          // Tell ChatInput to clear the liveTranscript bar for the new turn
          optionsRef.current.onTranscriptUpdate?.('', false);
          break;

        // VAD: user stopped speaking
        case 'input_audio_buffer.speech_stopped':
          isListeningRef.current = false;  // sync — before React batch
          setIsListening(false);
          break;

        // Conversation item created — may contain user transcript when
        // input_audio_transcription fires before the delta events.
        case 'conversation.item.created': {
          const item = event.item as { type?: string; role?: string; content?: Array<{type?: string; transcript?: string}> } | undefined;
          if (item?.role === 'user' && Array.isArray(item.content)) {
            const audioContent = item.content.find((c) => c.type === 'input_audio' && c.transcript);
            if (audioContent?.transcript) {
              currentTranscriptRef.current = audioContent.transcript;
              setTranscript(audioContent.transcript);
              optionsRef.current.onTranscriptUpdate?.(audioContent.transcript, false);
            }
          }
          break;
        }

        // Live / final transcription
        case 'conversation.item.input_audio_transcription.delta': {
          const delta = (event.delta as string | undefined) ?? '';
          currentTranscriptRef.current += delta;
          setTranscript(currentTranscriptRef.current);
          optionsRef.current.onTranscriptUpdate?.(currentTranscriptRef.current, false);
          break;
        }
        case 'conversation.item.input_audio_transcription.completed': {
          const finalText = (event.transcript as string | undefined) ?? currentTranscriptRef.current;
          currentTranscriptRef.current = finalText;
          setTranscript(finalText);
          optionsRef.current.onTranscriptUpdate?.(finalText, true);
          // Deferred sync: if response.done already fired (deferredAiResponseRef set)
          // but we had no transcript yet, trigger context sync now with the final text.
          if (deferredAiResponseRef.current !== null) {
            // Clear the 5-second safety timeout — we got the transcript in time.
            if (deferredSyncTimeoutRef.current !== null) {
              clearTimeout(deferredSyncTimeoutRef.current);
              deferredSyncTimeoutRef.current = null;
            }
            const aiResp = deferredAiResponseRef.current;
            deferredAiResponseRef.current = null;
            void runContextSync(finalText, aiResp);
          }
          break;
        }

        // Streaming audio output chunks (audio modality)
        case 'response.audio.delta': {
          const chunk = (event.delta as string | undefined) ?? '';
          if (chunk) optionsRef.current.onAudioChunk?.(chunk);
          break;
        }

        // Streaming response tokens (text modality)
        case 'response.text.delta': {
          const tok = (event.delta as string | undefined) ?? '';
          currentResponseRef.current += tok;
          setResponseText(currentResponseRef.current);
          optionsRef.current.onResponseToken?.(tok);
          break;
        }

        // Streaming response tokens (audio modality — transcript of spoken AI response)
        case 'response.audio_transcript.delta': {
          const tok = (event.delta as string | undefined) ?? '';
          currentResponseRef.current += tok;
          setResponseText(currentResponseRef.current);
          optionsRef.current.onResponseToken?.(tok);
          break;
        }
        case 'response.audio_transcript.done':
          // Final transcript of the audio response — no action needed,
          // deltas already accumulated the full text.
          break;

        // Response generation started
        case 'response.created':
          setIsResponding(true);
          currentResponseRef.current = '';
          setResponseText('');
          break;

        // Response complete — trigger context sync
        case 'response.done': {
          setIsResponding(false);
          const userTx = currentTranscriptRef.current;
          const aiResp = currentResponseRef.current;

          optionsRef.current.onTurnComplete?.(userTx, aiResp);

          // Reset for next turn
          currentTranscriptRef.current = '';
          currentResponseRef.current = '';
          setTranscript('');
          setResponseText('');

          // Deferred-sync strategy: Azure often sends transcription.completed
          // AFTER response.done.  If we already have a non-empty transcript
          // (transcription completed first), run sync immediately.  Otherwise
          // store aiResp in deferredAiResponseRef and let transcription.completed
          // trigger the sync.  A 5-second safety timeout ensures we never leak
          // a pending sync if transcription.completed never arrives (Azure error).
          if (userTx.trim()) {
            void runContextSync(userTx, aiResp);
          } else {
            deferredAiResponseRef.current = aiResp;
            // Safety timeout: if transcription.completed doesn't arrive within 5 s,
            // run sync with whatever partial transcript we have to avoid blocking
            // the next turn's context update.
            deferredSyncTimeoutRef.current = setTimeout(() => {
              if (deferredAiResponseRef.current !== null) {
                const fallbackTranscript = currentTranscriptRef.current || '[Transkription nicht verfügbar]';
                logger.warn('useRealtimeSession', 'transcription.completed timeout — syncing with fallback transcript');
                void runContextSync(fallbackTranscript, deferredAiResponseRef.current);
                deferredAiResponseRef.current = null;
                deferredSyncTimeoutRef.current = null;
              }
            }, 5000);
          }
          break;
        }

        // ── Function call: end_interview ──────────────────────────────────
        case 'response.function_call_arguments.done': {
          const fnName = (event as { name?: string }).name;
          const callId = (event as { call_id?: string }).call_id;
          if (fnName === 'end_interview') {
            logger.info('useRealtimeSession', 'end_interview function called by AI');
            // Acknowledge the function call so the model can generate a
            // final spoken goodbye (the prompt instructs it to say something
            // first, but the acknowledgement lets it complete the turn).
            if (callId) {
              sendEvent({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: callId,
                  output: '{"status": "ok"}',
                },
              });
              sendEvent({ type: 'response.create' });
            }
            // Give the AI ~3 seconds to deliver its goodbye audio/text,
            // then trigger the session-end callback.
            setTimeout(() => {
              optionsRef.current.onSessionEndRequested?.();
            }, 3000);
          }
          break;
        }

        // Azure session error
        case 'error': {
          const errMsg = (event.error as { message?: string } | undefined)?.message
            ?? 'Azure Realtime API error';
          logger.error('useRealtimeSession', 'Azure error event', event.error);
          emitError(errMsg);
          break;
        }

        // Session lifecycle acknowledgements — no action needed.
        case 'session.created':
        case 'session.updated':
        case 'input_audio_buffer.committed':
        case 'input_audio_buffer.cleared':
        case 'rate_limits.updated':
        case 'response.output_item.added':
        case 'response.output_item.done':
        case 'response.content_part.added':
        case 'response.content_part.done':
        case 'response.text.done':
        case 'response.audio.done':
        case 'conversation.item.input_audio_transcription.failed':
          break;

        default:
          // Log truly unknown events for diagnostics.
          logger.debug('useRealtimeSession', 'Unhandled event', event.type);
          break;
      }
    },
    [runContextSync, emitError],
  );

  // ── WebSocket open handler ────────────────────────────────────────────────

  // ── Connect ───────────────────────────────────────────────────────────────

  const connectWebSocket = useCallback(
    (): Promise<void> => {
      // Read from optionsRef to get the latest values after adapter.connect()
      // sets configRef — the destructured sessionId/workspaceId from the initial
      // render may still be empty strings at this point.
      const currentOpts = optionsRef.current;

      // Build the proxy WebSocket URL from the current browser origin
      const wsBase = window.location.origin
        .replace(/^https/, 'wss')
        .replace(/^http/, 'ws');
      const params = new URLSearchParams({
        sessionId: currentOpts.sessionId,
        workspaceId: currentOpts.workspaceId,
      });
      if (currentOpts.templateId) params.set('templateId', currentOpts.templateId);
      if (currentOpts.topic) params.set('topic', currentOpts.topic);
      if (currentOpts.audioOutput) params.set('audioOutput', 'true');
      if (currentOpts.voice) params.set('voice', currentOpts.voice);
      // Route to interview or voice WS proxy based on feature context
      const isInterview = !currentOpts.featureContext || currentOpts.featureContext === 'interview';
      const wsPath = isInterview
        ? '/api/python/interview/realtime/ws'
        : '/api/python/voice/realtime/ws';

      // Add feature-specific params for non-interview
      if (!isInterview) {
        params.set('feature', currentOpts.featureContext!);
        if (currentOpts.pageContext) params.set('pageContext', currentOpts.pageContext);
      }

      const wsUrl = `${wsBase}${wsPath}?${params.toString()}`;

      logger.debug('useRealtimeSession', 'Connecting to proxy', wsUrl);
      const connectTime = Date.now();
      let turnCount = 0;

      // Return a Promise that resolves once the WebSocket is OPEN.
      // This prevents audio chunks from being silently dropped while the
      // handshake (including backend system-prompt build) is still in flight.
      return new Promise<void>((resolve, reject) => {
        let settled = false;

        // Standard WebSocket to our server-side proxy (no ephemeral token needed)
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          const latencyMs = Date.now() - connectTime;
          logger.debug('useRealtimeSession', `Proxy connected (${latencyMs} ms)`);
          setIsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;
          // Server sends session.update to Azure automatically; no init needed here.
          if (!settled) { settled = true; resolve(); }
        };

        ws.onmessage = (msgEvent: MessageEvent<string>) => {
          try {
            const parsed = JSON.parse(msgEvent.data) as AzureRealtimeEvent;
            // Log ALL events for diagnostics (except high-frequency audio buffer appends)
            if (!parsed.type.startsWith('response.audio.delta') &&
                !parsed.type.startsWith('input_audio_buffer.')) {
              logger.debug('useRealtimeSession', 'Event', {
                type: parsed.type,
                turn: parsed.type === 'response.done' ? ++turnCount : undefined,
                detail: parsed.type.includes('transcript') ? JSON.stringify(parsed).slice(0, 200) : undefined,
              });
            }
            handleAzureEvent(parsed);
          } catch (err) {
            logger.warn('useRealtimeSession', 'Failed to parse Azure event', err);
          }
        };

        ws.onerror = () => {
          // onerror is always followed by onclose — handle reconnect there.
          logger.error('useRealtimeSession', 'WebSocket error');
          if (!settled) { settled = true; reject(new Error('WebSocket connection failed')); }
        };

        ws.onclose = (closeEvent) => {
          setIsConnected(false);
          setIsListening(false);
          setIsResponding(false);
          wsRef.current = null;

          logger.debug('useRealtimeSession', `Disconnected (code=${closeEvent.code}${
            isIntentionalCloseRef.current ? ', intentional' : ''
          })`);

          if (isIntentionalCloseRef.current) return;

          // Unexpected close — attempt reconnect with exponential backoff.
          const attempt = reconnectAttemptsRef.current;
          if (attempt < MAX_RECONNECT_ATTEMPTS) {
            const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt);
            logger.warn(
              'useRealtimeSession',
              `Unexpected close (code=${closeEvent.code}). Reconnecting in ${delay}ms (attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS})`,
            );
            reconnectAttemptsRef.current = attempt + 1;
            reconnectTimerRef.current = setTimeout(() => {
              void optionsRef.current; // keep linter happy — actual reconnect via connect()
              void connect();         // eslint-disable-line @typescript-eslint/no-use-before-define
            }, delay);
          } else {
            emitError(
              `Verbindung zu Azure Realtime API verloren (Code ${closeEvent.code}). Bitte Seite neu laden.`,
            );
          }
        };
      }); // end Promise
    },
    [handleAzureEvent, emitError],
  );

  const connect = useCallback(async (overrideToken?: string | null) => {
    // Cancel any pending reconnect timer.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    isIntentionalCloseRef.current = false;
    setError(null);

    // Close any existing socket.
    if (wsRef.current) {
      isIntentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      isIntentionalCloseRef.current = false;
    }

    // Open WebSocket and wait until it is actually OPEN before returning.
    // This prevents audio chunks from being silently dropped while the
    // WebSocket handshake (including backend system-prompt build) is still
    // in flight.  Auth token is not needed over WS — .NET handles auth.
    void overrideToken;
    await connectWebSocket();
  }, [connectWebSocket]);

  // ── Disconnect ────────────────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    isIntentionalCloseRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (deferredSyncTimeoutRef.current) {
      clearTimeout(deferredSyncTimeoutRef.current);
      deferredSyncTimeoutRef.current = null;
    }
    deferredAiResponseRef.current = null;
    wsRef.current?.close(1000, 'User disconnected');
    wsRef.current = null;
    setIsConnected(false);
    setIsListening(false);
    setIsResponding(false);
    setTranscript('');
    setResponseText('');
  }, []);

  // ── Cleanup on unmount ────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      isIntentionalCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (deferredSyncTimeoutRef.current) clearTimeout(deferredSyncTimeoutRef.current);
      wsRef.current?.close(1000, 'Component unmounted');
    };
  }, []);

  // ── Public audio controls ─────────────────────────────────────────────────

  const sendAudio = useCallback((base64Pcm16: string) => {
    sendEvent({ type: 'input_audio_buffer.append', audio: base64Pcm16 });
  }, [sendEvent]);

  /**
   * Like sendAudio, but only forwards the chunk when VAD has detected active
   * speech (i.e. between speech_started and speech_stopped).  Uses a ref
   * updated synchronously in the event handler — NOT React state — so there
   * is no batching lag at the start of a speech window.
   */
  const sendGatedAudio = useCallback((base64Pcm16: string) => {
    if (isListeningRef.current) {
      sendEvent({ type: 'input_audio_buffer.append', audio: base64Pcm16 });
    }
  }, [sendEvent]);

  const commitAudio = useCallback(() => {
    sendEvent({ type: 'input_audio_buffer.commit' });
  }, [sendEvent]);

  const requestResponse = useCallback(() => {
    sendEvent({ type: 'response.create' });
  }, [sendEvent]);

  /**
   * Dynamically toggle audio output and change voice without reconnecting.
   * Sends a `session.update` event with updated modalities and voice.
   */
  const updateAudioSettings = useCallback(
    (newAudioOutput: boolean, newVoice: string) => {
      sendEvent({
        type: 'session.update',
        session: {
          modalities: newAudioOutput ? ['text', 'audio'] : ['text'],
          voice: newVoice,
        },
      });
    },
    [sendEvent],
  );

  return {
    connect,
    disconnect,
    sendAudio,
    sendGatedAudio,
    commitAudio,
    requestResponse,
    updateAudioSettings,
    isConnected,
    isListening,
    isResponding,
    transcript,
    responseText,
    error,
  };
}
